import React, { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, euroFormat, formatApiErrorDetail, resolveImageUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Coins, Euro, FileText, ShoppingBag, Sparkles } from "lucide-react";

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";

export default function Shop() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [bestellungen, setBestellungen] = useState([]);
  const [selected, setSelected] = useState(null);
  const [bezahlung, setBezahlung] = useState("paypal"); // coins | paypal | rechnung
  const [menge, setMenge] = useState(1);
  const [coinsRabatt, setCoinsRabatt] = useState(0);
  const [adresse, setAdresse] = useState(user.anlagenadresse || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [p, b] = await Promise.all([api.get("/shop/produkte"), api.get("/me/shop-bestellungen")]);
    setItems(p.data);
    setBestellungen(b.data);
  };
  useEffect(() => { load(); }, []);

  // Gesamtpreise berechnen
  const calc = useMemo(() => {
    if (!selected) return null;
    const geldGesamt = (selected.geld_preis_brutto || 0) * menge;
    const coinsMax = Math.min(user.verfuegbare_coins || 0, Math.floor(geldGesamt));
    const coinsUsed = Math.min(coinsRabatt, coinsMax);
    const rest = Math.max(0, Math.round((geldGesamt - coinsUsed) * 100) / 100);
    const fcOnlyPossible = selected.coin_preis != null && (user.verfuegbare_coins || 0) >= selected.coin_preis * menge;
    return { geldGesamt, coinsMax, coinsUsed, rest, fcOnlyPossible };
  }, [selected, menge, coinsRabatt, user.verfuegbare_coins]);

  const finishBestellung = async (paypal_order_id = null) => {
    try {
      await api.post("/shop/bestellungen", {
        produkt_id: selected.id,
        bezahlung,
        lieferadresse: adresse,
        menge,
        coins_rabatt: bezahlung === "coins" ? 0 : calc?.coinsUsed || 0,
        paypal_order_id,
      });
      setSelected(null); setCoinsRabatt(0); setMenge(1);
      await load();
      await refresh();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Fehler bei Bestellung");
    }
  };

  const bestellenOhnePaypal = async () => {
    setError(""); setBusy(true);
    try { await finishBestellung(); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="shop-page">
      <div>
        <h1 className="text-3xl font-heading font-bold text-falke-blue inline-flex items-center gap-2"><ShoppingBag /> FalkeTek Shop</h1>
        <p className="text-falke-anthracite/70 mt-1">Zahle mit FalkeCoins, kombiniere mit PayPal oder bestelle auf Rechnung. 1 FC = 1 €.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
        {items.map((p) => (
          <div key={p.id} className="falke-card overflow-hidden falke-card-hover" data-testid={`shop-produkt-${p.id}`}>
            {p.bild_url && <div className="h-36 bg-falke-warmwhite flex items-center justify-center p-3"><img src={resolveImageUrl(p.bild_url)} alt="" className="max-h-full max-w-full object-contain" /></div>}
            <div className="p-4">
              {p.kategorie && <div className="text-[10px] uppercase tracking-wider text-falke-blue/60">{p.kategorie}</div>}
              <div className="font-heading text-falke-blue font-semibold mt-1">{p.name}</div>
              <div className="text-xs text-falke-anthracite/65 mt-1 line-clamp-2">{p.beschreibung}</div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                {p.geld_preis_brutto != null && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-falke-blue/10 text-falke-blue text-xs font-bold tabular"><Euro size={12} /> {euroFormat(p.geld_preis_brutto)}</span>
                )}
                {p.coin_preis != null && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-falke-orange/10 text-falke-orange text-xs font-bold tabular"><Coins size={12} /> {p.coin_preis} FC</span>
                )}
              </div>
              <button
                onClick={() => {
                  setSelected(p); setMenge(1); setCoinsRabatt(0); setError("");
                  setBezahlung(p.geld_preis_brutto != null ? "paypal" : "coins");
                }}
                className="btn-primary w-full mt-4 !py-2 text-sm"
                data-testid={`shop-buy-${p.id}`}
              >
                Bestellen
              </button>
            </div>
          </div>
        ))}
      </div>

      <h3 className="font-heading text-xl text-falke-blue mt-8">Meine Bestellungen</h3>
      {bestellungen.length === 0 ? (
        <div className="text-sm text-falke-anthracite/60 py-4">Noch keine Bestellungen.</div>
      ) : (
        <div className="falke-card divide-y divide-falke-blue/10">
          {bestellungen.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 p-5" data-testid={`shop-bestellung-${b.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-falke-blue">{b.produkt_name} <span className="text-falke-anthracite/60 text-sm">x {b.menge}</span></div>
                <div className="text-xs text-falke-anthracite/65 mt-0.5">
                  {b.bezahlung === "coins" && `${b.coins_abgezogen} FC`}
                  {b.bezahlung === "paypal" && <>PayPal · {euroFormat(b.geld_betrag_brutto || 0)}{b.coins_abgezogen > 0 && <> · {b.coins_abgezogen} FC Rabatt</>}</>}
                  {b.bezahlung === "rechnung" && <>Auf Rechnung · {euroFormat(b.geld_betrag_brutto || 0)}{b.coins_abgezogen > 0 && <> · {b.coins_abgezogen} FC Rabatt</>}</>}
                </div>
              </div>
              <span className={`status-badge ${b.status === "zugestellt" ? "bg-green-100 text-green-800" : b.status === "storniert" ? "bg-rose-100 text-rose-800" : b.status === "bezahlt" || b.status === "bestaetigt" ? "bg-emerald-100 text-emerald-800" : "bg-falke-blue/10 text-falke-blue"}`}>{b.status}</span>
            </div>
          ))}
        </div>
      )}

      {selected && calc && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="shop-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h3 className="font-heading text-2xl text-falke-blue">{selected.name}</h3>
              <button onClick={() => setSelected(null)} className="text-falke-blue/60 text-2xl leading-none" data-testid="shop-modal-close">×</button>
            </div>
            {selected.bild_url && <div className="h-32 bg-falke-warmwhite rounded-xl mb-3 flex items-center justify-center p-2"><img src={resolveImageUrl(selected.bild_url)} alt="" className="max-h-full max-w-full object-contain" /></div>}
            <p className="text-sm text-falke-anthracite/80 mb-4">{selected.beschreibung}</p>

            <label className="block text-sm font-medium text-falke-blue mb-1.5">Menge</label>
            <input type="number" min={1} value={menge} onChange={(e) => setMenge(Math.max(1, Number(e.target.value)))} className="w-24 px-3 py-2 rounded-xl border border-falke-blue/15 mb-3" data-testid="shop-menge" />

            <label className="block text-sm font-medium text-falke-blue mb-2">Zahlungsmethode</label>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {calc.fcOnlyPossible && (
                <button type="button" onClick={() => { setBezahlung("coins"); setCoinsRabatt(0); }} className={`px-2 py-2 rounded-xl border-2 text-xs font-semibold inline-flex flex-col items-center gap-1 ${bezahlung === "coins" ? "border-falke-orange bg-falke-orange/10" : "border-falke-blue/15"}`} data-testid="shop-pay-coins">
                  <Coins size={16} /> Nur FC<br/><span className="text-falke-orange">{selected.coin_preis * menge} FC</span>
                </button>
              )}
              {selected.geld_preis_brutto != null && (
                <>
                  <button type="button" onClick={() => setBezahlung("paypal")} className={`px-2 py-2 rounded-xl border-2 text-xs font-semibold inline-flex flex-col items-center gap-1 ${bezahlung === "paypal" ? "border-falke-orange bg-falke-orange/10" : "border-falke-blue/15"}`} data-testid="shop-pay-paypal">
                    <Sparkles size={16} /> PayPal
                  </button>
                  <button type="button" onClick={() => setBezahlung("rechnung")} className={`px-2 py-2 rounded-xl border-2 text-xs font-semibold inline-flex flex-col items-center gap-1 ${bezahlung === "rechnung" ? "border-falke-orange bg-falke-orange/10" : "border-falke-blue/15"}`} data-testid="shop-pay-rechnung">
                    <FileText size={16} /> Rechnung
                  </button>
                </>
              )}
            </div>

            {/* Coins-Rabatt-Slider — nur bei paypal/rechnung */}
            {(bezahlung === "paypal" || bezahlung === "rechnung") && calc.coinsMax > 0 && (
              <div className="mb-4 p-3 rounded-xl bg-falke-orange/[0.06] border border-falke-orange/20">
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-falke-blue inline-flex items-center gap-1.5"><Coins size={14} className="text-falke-orange" /> FalkeCoins als Rabatt</label>
                  <span className="text-sm tabular font-bold text-falke-orange">{calc.coinsUsed} FC</span>
                </div>
                <input type="range" min={0} max={calc.coinsMax} step={1} value={coinsRabatt} onChange={(e) => setCoinsRabatt(Number(e.target.value))} className="w-full accent-falke-orange" data-testid="shop-coins-slider" />
                <div className="flex justify-between text-[10px] text-falke-anthracite/60 mt-1 tabular">
                  <span>0 FC</span>
                  <span>max. {calc.coinsMax} FC</span>
                </div>
                <div className="text-xs text-falke-anthracite/65 mt-2">Du hast aktuell <span className="font-semibold text-falke-blue">{user.verfuegbare_coins} FC</span> · 1 FC = 1 € Rabatt</div>
              </div>
            )}

            <label className="block text-sm font-medium text-falke-blue mb-1.5">Lieferadresse</label>
            <textarea rows={2} value={adresse} onChange={(e) => setAdresse(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-falke-blue/15" data-testid="shop-adresse" />

            <div className="mt-4 p-3 rounded-xl bg-falke-blue/[0.04] text-sm space-y-1">
              {bezahlung === "coins" ? (
                <div className="flex justify-between font-bold">
                  <span>FalkeCoins</span>
                  <span className="tabular text-falke-orange">{selected.coin_preis * menge} FC</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between text-falke-anthracite/70">
                    <span>Produktpreis ({menge}×)</span>
                    <span className="tabular">{euroFormat(calc.geldGesamt)}</span>
                  </div>
                  {calc.coinsUsed > 0 && (
                    <div className="flex justify-between text-falke-orange">
                      <span>FalkeCoin-Rabatt</span>
                      <span className="tabular">– {euroFormat(calc.coinsUsed)} ({calc.coinsUsed} FC)</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-2 border-t border-falke-blue/10">
                    <span>Zu zahlen {bezahlung === "paypal" ? "(PayPal)" : "(Rechnung)"}</span>
                    <span className="tabular text-falke-blue">{euroFormat(calc.rest)}</span>
                  </div>
                </>
              )}
            </div>

            {error && <div className="text-sm text-red-600 mt-3" data-testid="shop-error">{error}</div>}

            {/* Submit */}
            {bezahlung === "paypal" && calc.rest > 0 ? (
              PAYPAL_CLIENT_ID ? (
                <div className="mt-4" data-testid="paypal-btn-wrap">
                  <PayPalScriptProvider
                    options={{ clientId: PAYPAL_CLIENT_ID, currency: "EUR", intent: "capture", components: "buttons", locale: "de_DE" }}
                  >
                    <PayPalButtons
                      style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                      disabled={!adresse}
                      forceReRender={[selected.id, menge, calc.coinsUsed, adresse]}
                      createOrder={async () => {
                        const r = await api.post("/shop/paypal/create", {
                          produkt_id: selected.id,
                          menge,
                          coins_rabatt: calc.coinsUsed,
                          lieferadresse: adresse,
                        });
                        return r.data.order_id;
                      }}
                      onApprove={async (data) => {
                        await api.post(`/shop/paypal/capture/${data.orderID}`);
                        await finishBestellung(data.orderID);
                      }}
                      onError={(err) => setError("PayPal-Fehler: " + (err?.message || "unbekannt"))}
                    />
                  </PayPalScriptProvider>
                </div>
              ) : (
                <div className="mt-4 text-sm text-rose-600">PayPal-Konfiguration fehlt (REACT_APP_PAYPAL_CLIENT_ID).</div>
              )
            ) : (
              <button onClick={bestellenOhnePaypal} disabled={busy || !adresse || (bezahlung === "paypal" && calc.rest > 0)} className="btn-primary w-full mt-4" data-testid="shop-confirm">
                {busy ? "Bestelle…" : bezahlung === "rechnung" ? "Auf Rechnung bestellen" : bezahlung === "coins" ? "Mit FalkeCoins bestellen" : "Verbindlich bestellen"}
              </button>
            )}
            {bezahlung === "rechnung" && (
              <p className="text-xs text-falke-anthracite/60 mt-3">FalkeTek meldet sich bei dir zur Rechnungsstellung. Zahlung binnen 14 Tagen.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
